import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Store, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Shell } from "@/components/Shell";
import { BUSINESS_TYPES, getBusiness, saveBusiness, getType, listBusinesses, setCurrentBusiness, addBusiness, removeBusiness } from "@/data/business";

export default function BusinessPage() {
  const navigate = useNavigate();
  const [list, setList] = useState(listBusinesses);
  const [b, setB] = useState(getBusiness);
  const [spacesText, setSpacesText] = useState(() => (getBusiness().spaces || []).join(", "));

  const load = (biz) => {
    setB(biz);
    setSpacesText((biz.spaces || []).join(", "));
  };
  const switchTo = (id) => {
    setCurrentBusiness(id);
    load(getBusiness());
    toast.success(`Attività attiva: ${getBusiness().name || "nuova attività"}`);
  };
  const add = () => {
    addBusiness();
    setList(listBusinesses());
    load(getBusiness());
  };
  const del = (id) => {
    if (list.length <= 1 || !window.confirm("Eliminare questa attività? Compilazioni e documenti collegati non saranno più visibili.")) return;
    removeBusiness(id);
    setList(listBusinesses());
    load(getBusiness());
  };

  const pickType = (id) => {
    const t = getType(id);
    setB({ ...b, type: id, tagline: t.desc });
    setSpacesText(t.spaces.join(", "));
  };

  const save = () => {
    if (!b.name.trim()) return;
    saveBusiness({ ...b, name: b.name.trim(), spaces: spacesText.split(",").map((s) => s.trim()).filter(Boolean) });
    toast.success("Attività salvata: il questionario è adattato alla tipologia");
    navigate("/");
  };

  return (
    <Shell header={<button className="btn btn-ghost !min-h-[44px] text-sm" onClick={() => navigate("/")} data-testid="business-back-btn"><ArrowLeft size={16} /> Elenco</button>}>
      <div className="fade-up">
        <div className="flex items-center gap-3"><Store className="text-[#2D4030]" size={24} /><p className="eyebrow">Impostazioni</p></div>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-[#2C221E] mt-2">L'attività</h1>
        <p className="text-[#5C4D46] mt-3 max-w-xl">Puoi seguire più attività: ognuna ha le sue compilazioni, schede evento e metodi. Scegli quella attiva, oppure aggiungine una nuova.</p>

        <div className="card p-5 sm:p-6 mt-8" data-testid="business-list">
          <div className="flex items-center justify-between gap-3 mb-4">
            <p className="eyebrow">Le tue attività</p>
            <button className="btn btn-olive !min-h-[44px] text-sm" onClick={add} data-testid="business-add-btn"><Plus size={16} /> Nuova attività</button>
          </div>
          <div className="grid gap-2">
            {list.map((x) => (
              <div key={x.id} className="flex items-center gap-3" data-testid={`business-row-${x.id}`}>
                <button className="choice flex-1 !min-h-[52px]" data-selected={x.id === b.id} onClick={() => switchTo(x.id)} data-testid={`business-switch-${x.id}`}>
                  <span className="choice-mark">{x.id === b.id && <Check size={16} strokeWidth={3} />}</span>
                  <span><span className="font-semibold">{x.name || "Nuova attività (senza nome)"}</span><span className="text-sm text-[#7A6B63]"> · {getType(x.type).label}</span></span>
                </button>
                {list.length > 1 && <button className="w-11 h-11 grid place-items-center rounded-full text-[#8A7970] hover:bg-[#F3ECE1]" onClick={() => del(x.id)} aria-label="Elimina" data-testid={`business-delete-${x.id}`}><Trash2 size={16} /></button>}
              </div>
            ))}
          </div>
        </div>

        <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#382E2B] mt-10">Dettagli dell'attività attiva</h2>

        <div className="card p-6 sm:p-9 mt-4 grid gap-7">
          <label className="block">
            <span className="eyebrow">Nome dell'attività</span>
            <input className="field mt-2 text-lg" value={b.name} onChange={(e) => setB({ ...b, name: e.target.value })} placeholder="es. Trattoria da Maria" data-testid="business-name-input" />
          </label>
          <label className="block">
            <span className="eyebrow">Sottotitolo</span>
            <input className="field mt-2" value={b.tagline || ""} onChange={(e) => setB({ ...b, tagline: e.target.value })} placeholder="es. Cucina di casa dal 1978" data-testid="business-tagline-input" />
          </label>
          <div>
            <p className="eyebrow mb-3">Tipologia</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {BUSINESS_TYPES.map((t) => (
                <button key={t.id} type="button" className="choice items-start" data-selected={b.type === t.id} onClick={() => pickType(t.id)} data-testid={`business-type-${t.id}`}>
                  <span className="choice-mark mt-0.5">{b.type === t.id && <Check size={16} strokeWidth={3} />}</span>
                  <span><span className="block font-semibold">{t.label}</span><span className="block text-sm text-[#7A6B63] mt-0.5">{t.desc}</span></span>
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="eyebrow">Spazi e servizi (separati da virgola)</span>
            <input className="field mt-2" value={spacesText} onChange={(e) => setSpacesText(e.target.value)} data-testid="business-spaces-input" />
          </label>
          <label className="block">
            <span className="eyebrow">Zona per la ricerca del territorio</span>
            <input className="field mt-2" value={b.zona || ""} onChange={(e) => setB({ ...b, zona: e.target.value })} placeholder="es. Chianti, Toscana · oppure: entro 40 km da Siena" data-testid="business-zona-input" />
          </label>
          <label className="block">
            <span className="eyebrow">Descrizione per l'AI (contesto, situazione, obiettivi)</span>
            <textarea className="field mt-2 min-h-[110px]" value={b.description || ""} onChange={(e) => setB({ ...b, description: e.target.value })} placeholder="es. Trattoria di famiglia, 60 coperti, lo chef è il titolare, vogliono aprire anche a pranzo…" data-testid="business-description-input" />
          </label>
          <div className="flex justify-end">
            <button className="btn btn-primary text-lg !min-h-[60px] !px-9" onClick={save} disabled={!b.name.trim()} data-testid="business-save-btn"><Check size={20} /> Salva</button>
          </div>
        </div>
      </div>
    </Shell>
  );
}
